import { useState } from 'react';
import {
  Alert,
  Button,
  FormControl,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import {
  getSessionDrawerChrome,
  headerOutlinedButtonSx,
  sessionDrawerToggleGroupSx,
} from '@kangaroo/ui';

import type { LapTimingMode, useSessionLapSlot } from '../../features/sessions/useSessionLapSlot';
import { SessionCircuitSetupDialog } from './SessionCircuitSetupDialog';
import { SessionCircuitPicker } from './SessionCircuitPicker';

const LAP_TIMING_MODE_HELP: Record<LapTimingMode, string> = {
  mychron:
    'Raw lap times from the logger — one lap each time the device recorded a lap beacon.',
  circuit:
    'Uses the saved start/finish geometry to calculate circuit laps.',
};

type SessionsLapTimingControlsProps = {
  lapTimingMode: LapTimingMode;
  onLapTimingModeChange: (mode: LapTimingMode) => void;
  setupSlot: ReturnType<typeof useSessionLapSlot>;
  setupDialogTitle: string;
};

export function SessionsLapTimingControls({
  lapTimingMode,
  onLapTimingModeChange,
  setupSlot,
  setupDialogTitle,
}: SessionsLapTimingControlsProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const chrome = getSessionDrawerChrome(isDark);
  const [circuitDialogOpen, setCircuitDialogOpen] = useState(false);
  const selectedContributionId = setupSlot.sessionContext?.contributionId ?? '';

  return (
    <>
      <Stack spacing={0.75}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={lapTimingMode}
            onChange={(_, value: LapTimingMode | null) => {
              if (value) {
                onLapTimingModeChange(value);
              }
            }}
            sx={sessionDrawerToggleGroupSx(isDark)}
          >
            <ToggleButton value="circuit">Circuit Laps</ToggleButton>
            <ToggleButton value="mychron">Raw laps</ToggleButton>
          </ToggleButtonGroup>

          {lapTimingMode === 'circuit' ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<TuneOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={() => setCircuitDialogOpen(true)}
              sx={{
                ...headerOutlinedButtonSx(isDark),
                textTransform: 'none',
                fontSize: '0.68rem',
                whiteSpace: 'nowrap',
              }}
            >
              Circuit setup
            </Button>
          ) : null}
        </Stack>

        <Typography sx={{ fontSize: '0.62rem', color: chrome.body, lineHeight: 1.45 }}>
          {LAP_TIMING_MODE_HELP[lapTimingMode]}
        </Typography>

        {lapTimingMode === 'circuit' ? (
          <FormControl fullWidth size="small" disabled={!setupSlot.canEdit || setupSlot.contributionsLoading}>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: chrome.label, mb: 0.5 }}>
              Circuit
            </Typography>
            <SessionCircuitPicker
              options={setupSlot.circuitPickerOptions}
              selectedContributionId={selectedContributionId}
              disabled={!setupSlot.canEdit || setupSlot.contributionsLoading}
              onChange={(contributionId) => void setupSlot.assignCircuit(contributionId)}
            />
            {setupSlot.suggestedCircuit && !selectedContributionId ? (
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mt: 0.6 }}>
                <Typography sx={{ fontSize: '0.62rem', color: chrome.body, lineHeight: 1.4 }}>
                  Best match: {[setupSlot.suggestedCircuit.venue, setupSlot.suggestedCircuit.layoutName]
                    .filter(Boolean).join(' · ')}
                  {setupSlot.suggestedCircuitMatch
                    ? ` · ${Math.round(setupSlot.suggestedCircuitMatch.confidence * 100)}% GPS/shape · ${Math.round(setupSlot.suggestedCircuitMatch.meanDistanceM)} m mean gap`
                    : ' · track metadata'}
                </Typography>
                {setupSlot.canEdit ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => void setupSlot.assignCircuit(setupSlot.suggestedCircuit!.id)}
                    sx={{ flexShrink: 0, textTransform: 'none', fontSize: '0.62rem' }}
                  >
                    Use match
                  </Button>
                ) : null}
              </Stack>
            ) : setupSlot.previewContribution && setupSlot.circuitSourceLabel ? (
              <Typography sx={{ fontSize: '0.62rem', color: chrome.body, lineHeight: 1.4, mt: 0.5 }}>
                Previewing {setupSlot.previewContribution.venue?.trim() || 'circuit'} · {setupSlot.circuitSourceLabel}
              </Typography>
            ) : null}
            {!setupSlot.canEdit && !setupSlot.publicDemo ? (
              <Alert severity="info" sx={{ mt: 0.75, py: 0.35 }}>
                Session editing is read-only. Go to Billing to subscribe or apply a partner code.
              </Alert>
            ) : setupSlot.publicDemo ? (
              <Alert severity="info" sx={{ mt: 0.75, py: 0.35 }}>
                Demo reslices are temporary and reset when you leave this session.
              </Alert>
            ) : null}
          </FormControl>
        ) : null}
      </Stack>

      <SessionCircuitSetupDialog
        open={circuitDialogOpen}
        onClose={() => setCircuitDialogOpen(false)}
        title={setupDialogTitle}
        slot={setupSlot}
      />
    </>
  );
}
